// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Calculator config type definition.
 * @author James Reid
 */

// @ts-check

// @@imports-types
/* eslint-disable no-unused-vars -- Type imports. */
import {
    JoinType,
    TrimType,
    TrimColor,
    TrimDepth,
    MembraneType,
    SubstrateType,
    SeamDirection
} from "./Enums.js"
import { UnitType } from "./Units.js"
/* eslint-enable no-unused-vars -- Close disable-enable pair. */

// @@body
/**
 * 
 * @typedef {object} AdhesiveCoverage // move elsewhere... 
 * @property {number} default
 * @property {number} min
 * @property {number} max
 * @property {number} client
 * @property {UnitType} unitType
 */

/**
 * 
 * @typedef {object} ConfigAdhesiveCoverages
 * @property {object} deck
 * @property {AdhesiveCoverage} deck.waterBased
 * @property {AdhesiveCoverage} deck.contactBonding
 * @property {AdhesiveCoverage} deck.pu
 * @property {AdhesiveCoverage} primer
 * @property {object} sealants
 * @property {AdhesiveCoverage} sealants.lapEdge
 * @property {AdhesiveCoverage} sealants.waterBlock
 * @property {AdhesiveCoverage} sealants.msPolymer
 * @property {AdhesiveCoverage} sealants.blackSilicone
 */

/**
 * 
 * @typedef {object} ConfigClientLimits
 * @property {object} roll
 * @property {number} roll.width
 * @property {number} roll.weight
 * @property {object} roll.waste
 * @property {number} roll.waste.upper
 * @property {number} roll.waste.lower
 * @property {object} adhesive
 * @property {object} adhesive.waste
 * @property {number} adhesive.waste.upper
 * @property {number} adhesive.waste.lower
 */

/**
 * 
 * @typedef {object} MembraneOverhang
 * @property {number} default
 * @property {number} min
 * @property {number} client
 */

/**
 * 
 * @typedef {object} ConfigOverhangs
 * @property {MembraneOverhang} houseWall
 * @property {MembraneOverhang} parapetWall
 * @property {MembraneOverhang} gutterDrip
 * @property {MembraneOverhang} raisedKerb
 * @property {MembraneOverhang} pitchRoofOn
 * @property {MembraneOverhang} pitchRoofOff
 * @property {MembraneOverhang} feltJoin
 * @property {MembraneOverhang} seamJoin
 * @property {MembraneOverhang} buttJoin
 * @property {MembraneOverhang} fleeceBackedJoin
 * @property {MembraneOverhang} none
 * @property {MembraneOverhang} default
 */

/**
 * 
 * @typedef {object} ConfigParapet
 * @property {number} width
 * @property {number} height
 */

/**
 * 
 * @typedef {object} ConfigMembrane
 * @property {MembraneType} type
 * @property {number} thickness
 * @property {number} rollLength
 * @property {string} title
 * @property {string} id
 */

/**
 * 
 * @typedef {object} ConfigTapes
 * @property {number} overlap
 * @property {number} primerBorder
 * @property {boolean} addSealant
 * @property {boolean} precutPatches
 */

/**
 * 
 * @typedef {object} ConfigExtraAllowances
 * @property {number} tape
 * @property {number} adhesive
 */

/**
 * 
 * @typedef {object} ConfigTrims
 * @property {TrimColor} color
 * @property {TrimType} type
 * @property {TrimDepth} depth
 * @property {number} minLength
 * @property {number} maxLength
 */

/**
 * 
 * @typedef {object} Config
 * @property {ConfigAdhesiveCoverages} adhesiveCoverages
 * @property {ConfigClientLimits} clientLimits
 * @property {ConfigOverhangs} overhangs
 * @property {ConfigParapet} parapet
 * @property {ConfigMembrane} membrane
 * @property {SeamDirection} seamDirection
 * @property {SubstrateType} substrate
 * @property {JoinType} rubberJoin
 * @property {number} contactBorderDepth
 * @property {ConfigTapes} tapes
 * @property {ConfigExtraAllowances} extraAllowances
 * @property {ConfigTrims} trims
 */

// @@exports
/**
 * @type {Config}
 * @ignore
 */
export let Config

/**
 * @type {ConfigAdhesiveCoverages}
 * @ignore
 */
export let ConfigAdhesiveCoverages

/**
 * @type {ConfigClientLimits}
 * @ignore
 */
export let ConfigClientLimits

/**
 * @type {ConfigOverhangs}
 * @ignore
 */
export let ConfigOverhangs

/**
 * @type {ConfigParapet}
 * @ignore
 */
export let ConfigParapet

/**
 * @type {ConfigMembrane}
 * @ignore
 */
export let ConfigMembrane

/**
 * @type {ConfigTapes}
 * @ignore
 */
export let ConfigTapes

/**
 * @type {ConfigExtraAllowances}
 * @ignore
 */
export let ConfigExtraAllowances

/**
 * @type {ConfigTrims}
 * @ignore
 */
export let ConfigTrims
